import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { categoryService } from "@/services/categoryService";
import {
  CategoryFilters,
  CreateCategoryDto,
  UpdateCategoryDto,
  ReorderDto,
} from "@/services/interfaces/ICategoryService";

export const categoryKeys = {
  all: ["categories"] as const,
  lists: () => [...categoryKeys.all, "list"] as const,
  list: (filters?: CategoryFilters) =>
    [...categoryKeys.lists(), filters] as const,
};

const DASHBOARD_QUERY_KEY = ["dashboard"] as const;

const invalidateCategoryRelatedQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: categoryKeys.all });
  queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
};

export const useCategories = (filters: CategoryFilters) => {
  return useQuery({
    queryKey: categoryKeys.list(filters),
    queryFn: () => categoryService.findAll(filters),
    enabled: !!filters?.hotelId && !!filters?.entityType,
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCategoryDto) => categoryService.create(data),
    onSuccess: () => {
      invalidateCategoryRelatedQueries(queryClient);
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryDto }) =>
      categoryService.update(id, data),
    onSuccess: () => {
      invalidateCategoryRelatedQueries(queryClient);
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => categoryService.deleteCategory(id),
    onSuccess: () => {
      invalidateCategoryRelatedQueries(queryClient);
    },
  });
};

export const useReorderCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReorderDto) => categoryService.reorder(data),
    onSuccess: () => {
      invalidateCategoryRelatedQueries(queryClient);
    },
  });
};
